import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { formatCurrency, formatMilli } from '../../utils/formatUtils';
import { 
  Users, Wallet, Scale, Download, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminOutstandingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getOutstandingReport();
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load outstanding report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const downloadCSV = async () => {
    try {
      const response = await adminAPI.downloadOutstandingCSV();
      const { csv_content, filename } = response.data;
      
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('CSV downloaded!');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-outstanding-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Outstanding Report</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV}>
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-gold/20 bg-gold/5">
              <CardContent className="p-4 text-center">
                <Scale className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Total Outstanding</p>
                <p className="font-bold text-xl text-navy">{report.totals.total_millis?.toFixed(2)} Milli's</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <Wallet className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Total Value Received</p>
                <p className="font-bold text-xl text-green-600">{formatCurrency(report.totals.total_received)}</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Refunded Value</p>
                <p className="font-bold text-xl text-orange-600">{formatCurrency(report.totals.total_refunded)}</p>
              </CardContent>
            </Card>
            <Card className="border-navy/20 bg-navy/5">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Outstanding Due (INR)</p>
                <p className="font-bold text-xl text-navy">{formatCurrency(report.totals.total_outstanding_value)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Customer List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : report?.customers?.length === 0 ? (
          <Card className="border-gold/20">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No outstanding balances found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {report?.customers?.map((customer) => (
              <Card key={customer.user_id} className="border-gold/20">
                <CardContent className="p-0">
                  <div 
                    className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleExpand(customer.user_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{customer.name}</p>
                          <p className="text-xs text-muted-foreground">{customer.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-navy">{customer.outstanding_millis?.toFixed(2)} Milli's</p>
                          <p className="text-sm text-muted-foreground">{formatCurrency(customer.outstanding_value)}</p>
                        </div>
                        {expandedUsers[customer.user_id] ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {expandedUsers[customer.user_id] && (
                    <div className="border-t border-border p-4 bg-muted/30">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Total Bought</p>
                          <p className="font-medium text-green-600">{customer.total_bought_millis?.toFixed(2)} Milli's</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(customer.total_received)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total Refunded</p>
                          <p className="font-medium text-orange-600">{customer.total_refunded_millis?.toFixed(2)} Milli's</p>
                          <p className="text-xs text-muted-foreground">{formatCurrency(customer.total_refunded_value)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total Redeemed</p>
                          <p className="font-medium text-purple-600">{customer.total_redeemed_millis?.toFixed(2)} Milli's</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg. Buy Rate</p>
                          <p className="font-medium text-navy">{formatCurrency(customer.avg_buy_rate)}/Milli</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOutstandingPage;
